import { defineStore } from 'pinia'
import type { Request, Single, EstimationMode } from '@/lib/dd-queue/types'
import type { SubjectMutation, ProofVersion, DesignFile } from '@/lib/dd-queue/types/subjectMutation'
import type { EstimationUpdate } from '@/lib/dd-queue/types/estimationUpdate'
import type { DdOriginPayload } from '@/lib/dd-queue/types/originPayload'
import { originPayloadToRequest } from '@/lib/dd-queue/originAdapter'
import { COLUMN_DEFS } from '@/lib/dd-queue/data/columns'
import { HISTORY_COLUMN_DEFS } from '@/lib/dd-queue/data/historyColumns'
import type { SortDir } from '@/lib/dd-queue/sort'
import { EMPTY_FILTERS, type FilterState } from '@/lib/dd-queue/filters'
import { ddQueueCurrentOperator } from '@/lib/dd-queue/ddQueueIdentity'
import { resolveSubject } from '@/lib/dd-queue/resolveSubject'
import {
  stepByKey,
  advanceDeliverable,
  autoCompleteOpen,
  buildStepReactivation,
  resolveOpenDeliverables,
} from '@/lib/dd-queue/workflow'
import type { DeliverableEntry, ChatMessage } from '@/lib/dd-queue/types'
import { resolveDdPermitFromPermissions, type HasPermissionFn } from '@/utils/swDdAccess'
import { arrayMove } from '@/lib/dd-queue/bulkSelection'
import {
  loadDdPinnedView,
  saveDdPinnedView,
  clearDdPinnedView,
  type DdPinnedView,
} from '@/lib/dd-queue/pinnedView'
import { useToast } from 'vue-toastification'
import {
  fetchDesignDevelopmentQueueIntake,
  type DesignDevelopmentQueueIntakeMeta,
} from '@/services/designDevelopmentQueueIntakeApi'
import {
  listDesignDevelopmentQueueFiles,
  uploadDesignDevelopmentQueueFile,
  deleteDesignDevelopmentQueueFile,
  type DesignDevelopmentQueueFileApi,
} from '@/services/designDevelopmentQueueFilesApi'
import {
  DdWorkflowConflictError,
  fetchDdQueueState,
  isEmptyWorkflowSnapshot,
  postDdWorkflowCancel,
  postDdWorkflowComplete,
  postDdWorkflowReactivate,
  postDdWorkflowRestore,
  postDdWorkflowChat,
  postDdWorkflowChatReply,
  putDdWorkflowAssignment,
  postDdWorkflowTimerStart,
  postDdWorkflowTimerStop,
  putDdWorkflowPacket,
  putDdWorkflowLineEditor,
  putDdWorkflowEstimationMode,
  workflowSnapshotToMutationPatch,
  chatMessageFromSnapshot,
  type DdWorkflowLineSnapshot,
} from '@/services/designDevelopmentWorkflowApi'
import { dueFieldsFromIso } from '@/lib/dd-queue/dueAt'

/** Max pages when hydrating from queue-intake (per_page=100 → ≤1000 rows). */
export const DD_QUEUE_INTAKE_MAX_PAGES = 10

export type DdQueueIntakeLoadMeta = {
  loaded: number
  skippedHubspotOnly: number
  errors: number
  totalFromApi: number
}

const _pin = loadDdPinnedView()

/** Shared in-flight gate for ensureQueueLoaded (cross-page; cleared on invalidate). */
let intakeLoadInFlight: Promise<DdQueueIntakeLoadMeta> | null = null

function me(): string {
  return ddQueueCurrentOperator().name
}

/**
 * A1 — Prefer line.due_at over submission ingest stamp.
 * Master display uses earliest child due; request fields updated in place.
 */
function applyLineDueToRequests(
  requests: Request[],
  mutations: Record<string, SubjectMutation>
): Request[] {
  return requests.map((req) => {
    const skus = req.isMaster ? req.children.map((c) => c.sku) : [req.sku]
    const isos: string[] = []
    for (const sku of skus) {
      const iso = mutations[`${req.id}:${sku}`]?.dueAtIso
      if (iso) isos.push(iso)
    }
    if (isos.length === 0) return req
    isos.sort()
    return { ...req, ...dueFieldsFromIso(isos[0]!) }
  })
}

/**
 * ROUTING-E1 — fold line quote SoT into request overlay (quote # / terms / tax / URL / cents map).
 * ROUTING-E3 — also wire dealId from hubspot_deal_id.
 */
function applyLineQuoteToRequests(
  requests: Request[],
  mutations: Record<string, SubjectMutation>
): Request[] {
  return requests.map((req) => {
    const skus = req.isMaster ? req.children.map((c) => c.sku) : [req.sku]
    let quoteNumber = req.quoteNumber
    let paymentTerms = req.paymentTerms
    let taxRate = req.taxRate
    let piqEstimateUrl = req.piqEstimateUrl
    let dealId = req.dealId
    const piqPricingCents: Record<string, number> = { ...(req.piqPricingCents ?? {}) }
    const piqPricing: Record<string, number> = { ...(req.piqPricing ?? {}) }
    let touched = false

    for (const sku of skus) {
      const m = mutations[`${req.id}:${sku}`]
      if (!m) continue

      if (m.quoteNumber !== undefined) {
        quoteNumber = m.quoteNumber || undefined
        touched = true
      }
      if (m.paymentTerms !== undefined) {
        paymentTerms = m.paymentTerms || undefined
        touched = true
      }
      if (m.taxRate !== undefined) {
        taxRate = m.taxRate ?? undefined
        touched = true
      }
      if (m.quoteUrl !== undefined) {
        piqEstimateUrl = m.quoteUrl || undefined
        touched = true
      }
      if (m.hubspotDealId !== undefined) {
        dealId = m.hubspotDealId || undefined
        touched = true
      }
      if (m.unitPriceCents !== undefined) {
        touched = true
        if (m.unitPriceCents == null) {
          delete piqPricingCents[sku]
          delete piqPricing[sku]
        } else {
          piqPricingCents[sku] = m.unitPriceCents
          piqPricing[sku] = m.unitPriceCents / 100
        }
      }
    }

    if (!touched) return req
    return {
      ...req,
      quoteNumber,
      paymentTerms,
      taxRate,
      piqEstimateUrl,
      dealId,
      piqPricingCents:
        Object.keys(piqPricingCents).length > 0 ? piqPricingCents : undefined,
      piqPricing: Object.keys(piqPricing).length > 0 ? piqPricing : undefined,
    }
  })
}

function applyLineOverlaysToRequests(
  requests: Request[],
  mutations: Record<string, SubjectMutation>
): Request[] {
  return applyLineQuoteToRequests(applyLineDueToRequests(requests, mutations), mutations)
}

function toastWorkflowError(message: string) {
  try {
    useToast().error(message)
  } catch {
    /* unit tests / no toast host */
  }
}

let _proofIdNonce = 0
let _designFileNonce = 0
let _chatIdNonce = 0
function genProofId(): string {
  _proofIdNonce += 1
  return `pv-${Date.now()}-${_proofIdNonce}`
}
function genDesignFileId(): string {
  _designFileNonce += 1
  return `df-${Date.now()}-${_designFileNonce}`
}
function genChatId(): string {
  _chatIdNonce += 1
  return `cm-${Date.now()}-${_chatIdNonce}`
}

function stampOpenedAt(
  _prev: Record<string, DeliverableEntry>,
  next: Record<string, DeliverableEntry>,
  existing: Record<string, number> | undefined
): Record<string, number> {
  const out = { ...(existing ?? {}) }
  const now = Date.now()
  for (const [k, entry] of Object.entries(next)) {
    if (entry.status === 'open' && out[k] == null) out[k] = now
  }
  return out
}

export const useDdQueueStore = defineStore('ddQueue', {
  state: () => ({
    ingestedRequests: [] as Request[],
    subjectMutations: {} as Record<string, SubjectMutation>,
    peekId: null as string | null,
    peekChildSku: null as string | null,
    fullRequestId: null as string | null,
    fullRequestSku: null as string | null,
    quoteRequestId: null as string | null,
    expandedMasters: {} as Record<string, true>,
    sort: { col: 'due', dir: 'asc' as SortDir } as { col: string; dir: SortDir } | null,
    columnOrder: COLUMN_DEFS.map((c) => c.id),
    columnWidths: Object.fromEntries(COLUMN_DEFS.map((c) => [c.id, c.defaultWidth])),
    hiddenColumns: Object.fromEntries(
      COLUMN_DEFS.filter((c) => c.defaultHidden).map((c) => [c.id, true as const])
    ) as Record<string, true>,
    /** Reference queue-store L258–263 — independent from queue columns */
    historyColumnOrder: HISTORY_COLUMN_DEFS.map((c) => c.id),
    historyColumnWidths: Object.fromEntries(
      HISTORY_COLUMN_DEFS.map((c) => [c.id, c.defaultWidth])
    ),
    historyHiddenColumns: Object.fromEntries(
      HISTORY_COLUMN_DEFS.filter((c) => c.defaultHidden).map((c) => [c.id, true as const])
    ) as Record<string, true>,
    historySort: null as { col: string; dir: SortDir } | null,
    /** Reference queue-store L226–231 */
    selectedBulk: {} as Record<string, true>,
    lastBulkClickIndex: null as number | null,
    /** Supervisor tier — derived from products_design.supervisor.view via syncPermitFromAuth */
    permit: 'user' as 'supervisor' | 'user',
    pinnedView: _pin as DdPinnedView | null,
    /** Reference queue-store L408 — hydrate filters/role/scope from pin on cold load */
    role: _pin?.role ?? 'all',
    scope: _pin?.scope ?? 'all',
    search: _pin?.search ?? '',
    filters: { ...(_pin?.filters ?? EMPTY_FILTERS) } as FilterState,
    /**
     * DD-BE-PR1: sales_workflow_submissions → Request rows via originAdapter.
     * DD-BE-PR2b-FILES: design-file / proof bytes persist server-side (lazy hydrate on overlay).
     * Workflow state (assign/complete/chat/timers) remains session-only → later PR2b slices.
     */
    intakeLoading: false,
    intakeError: null as string | null,
    intakeMeta: null as DdQueueIntakeLoadMeta | null,
    intakeLastPageMeta: null as DesignDevelopmentQueueIntakeMeta | null,
    /** DD-FRESHNESS-001: true after a successful ensureQueueLoaded until invalidate/force. */
    intakeLoaded: false,
  }),

  getters: {
    allRequests(state): Request[] {
      return state.ingestedRequests
    },

    visibleColumnIds(state): string[] {
      return state.columnOrder.filter((id) => !state.hiddenColumns[id])
    },

    gridTemplateColumns(state): string {
      return state.columnOrder
        .filter((id) => !state.hiddenColumns[id])
        .map((id) => {
          const def = COLUMN_DEFS.find((c) => c.id === id)
          const w = state.columnWidths[id] ?? def?.defaultWidth ?? 80
          return `${w}px`
        })
        .join(' ')
    },

    historyVisibleColumnIds(state): string[] {
      return state.historyColumnOrder.filter((id) => !state.historyHiddenColumns[id])
    },

    historyGridTemplateColumns(state): string {
      return state.historyColumnOrder
        .filter((id) => !state.historyHiddenColumns[id])
        .map((id) => {
          const def = HISTORY_COLUMN_DEFS.find((c) => c.id === id)
          const w = state.historyColumnWidths[id] ?? def?.defaultWidth ?? 80
          return `${w}px`
        })
        .join(' ')
    },

    hasActiveFilters(state): boolean {
      return (
        state.search.trim() !== '' ||
        Object.values(state.filters).some((v) => v !== '')
      )
    },
  },

  actions: {
    ingestPayload(payload: DdOriginPayload): string | null {
      const req = originPayloadToRequest(payload)
      if (!req) return null
      const dup = this.ingestedRequests.find((r) => r.id === req.id)
      if (dup) return dup.id
      this.ingestedRequests = [req, ...this.ingestedRequests]
      return req.id
    },

    /**
     * DD-BE-PR1: hydrate ingestedRequests from GET /v1/design-development/queue-intake.
     * DD-FRESHNESS-001: full reload replaces server-backed rows (keeps memory-only fixtures
     * without submissionUuid) so deletes/submits appear without F5; swap is atomic at end
     * (stale rows stay visible until the new list is ready).
     */
    async loadQueueFromServer(): Promise<DdQueueIntakeLoadMeta> {
      this.intakeLoading = true
      this.intakeError = null
      const stats: DdQueueIntakeLoadMeta = {
        loaded: 0,
        skippedHubspotOnly: 0,
        errors: 0,
        totalFromApi: 0,
      }
      try {
        const fixtures = this.ingestedRequests.filter((r) => !r.submissionUuid)
        const serverOrdered: Request[] = []
        const seen = new Set<string>()

        let page = 1
        let lastPage = 1
        do {
          const envelope = await fetchDesignDevelopmentQueueIntake(page, 100)
          this.intakeLastPageMeta = envelope.meta
          lastPage = envelope.meta.last_page
          stats.totalFromApi = envelope.meta.total
          for (const item of envelope.data) {
            try {
              const raw = { ...(item.payload ?? {}) } as Record<string, unknown>
              if (typeof raw.ref !== 'string' || !raw.ref) {
                raw.ref = item.origin_ref
              }
              if (typeof raw.tile !== 'string') {
                raw.tile = item.tile
              }
              const req = originPayloadToRequest(raw as unknown as DdOriginPayload)
              if (req == null) {
                stats.skippedHubspotOnly += 1
                continue
              }
              if (seen.has(req.id)) continue
              seen.add(req.id)
              // Pre-line: prefer server intake due_at over SPA calculator.
              const withDue =
                item.due_at != null && item.due_at !== ''
                  ? { ...req, ...dueFieldsFromIso(item.due_at), submissionUuid: item.uuid }
                  : { ...req, submissionUuid: item.uuid }
              serverOrdered.push(withDue)
              stats.loaded += 1
            } catch {
              stats.errors += 1
            }
          }
          page += 1
        } while (page <= lastPage && page <= DD_QUEUE_INTAKE_MAX_PAGES)

        const fixtureOnly = fixtures.filter((r) => !seen.has(r.id))
        const nextRequests: Request[] = [...serverOrdered, ...fixtureOnly]
        const keepIds = new Set(nextRequests.map((r) => r.id))
        const prunedMutations = Object.fromEntries(
          Object.entries(this.subjectMutations).filter(([k]) => keepIds.has(k.split(':')[0] ?? ''))
        )

        const uuids = nextRequests
          .map((r) => r.submissionUuid)
          .filter((u): u is string => typeof u === 'string' && u.length > 0)

        let nextMutations = prunedMutations
        let hydratedRequests = nextRequests
        if (uuids.length > 0) {
          try {
            nextMutations = await this.hydrateWorkflowState(uuids, {
              requests: nextRequests,
              baseMutations: prunedMutations,
              commit: false,
            })
            // A1: line snapshot due_at overwrites submission ingest stamp.
            hydratedRequests = applyLineOverlaysToRequests(nextRequests, nextMutations)
          } catch {
            /* workflow hydrate is best-effort; intake still succeeded */
          }
        }

        // Atomic reveal: rows + workflow mutations land in the same tick (DD-QUEUE-BOOT-FLICKER-FIX).
        this.ingestedRequests = hydratedRequests
        this.subjectMutations = nextMutations

        this.intakeMeta = stats
        return stats
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load queue from server'
        this.intakeError = message
        this.intakeMeta = stats
        throw err
      } finally {
        this.intakeLoading = false
      }
    },

    /**
     * DD-FRESHNESS-001: mark intake stale so the next ensureQueueLoaded refetches.
     * Does not clear visible rows (stale-while-revalidate until reload finishes).
     */
    invalidateQueue() {
      this.intakeLoaded = false
      intakeLoadInFlight = null
    },

    /**
     * DD-BE-PR2b-1-BOOT-FIX + DD-FRESHNESS-001: shared boot for all five D&D pages.
     * Refetches when stale (intakeLoaded false) or force=true; concurrent callers share
     * one in-flight promise; intra-module hops keep intakeLoaded true (no spam).
     */
    async ensureQueueLoaded(options?: { force?: boolean }): Promise<DdQueueIntakeLoadMeta> {
      const force = options?.force === true

      if (!force && this.intakeLoaded) {
        return (
          this.intakeMeta ?? {
            loaded: 0,
            skippedHubspotOnly: 0,
            errors: 0,
            totalFromApi: 0,
          }
        )
      }
      if (!force && intakeLoadInFlight) return intakeLoadInFlight

      const run = (): Promise<DdQueueIntakeLoadMeta> =>
        this.loadQueueFromServer()
          .then((meta) => {
            this.intakeLoaded = true
            return meta
          })
          .catch((err) => {
            this.intakeLoaded = false
            throw err
          })

      if (force) {
        this.intakeLoaded = false
        return run()
      }

      intakeLoadInFlight = run().finally(() => {
        intakeLoadInFlight = null
      })
      return intakeLoadInFlight
    },

    /**
     * DD-BE-PR2b-1: map server workflow lines → subjectMutations (non-destructive).
     * Skips empty lines so SPA seed deliverables remain until first mutation.
     * DD-QUEUE-BOOT-FLICKER-FIX: pass commit:false + requests/baseMutations to hydrate
     * off-screen, then assign with intake in one tick.
     */
    async hydrateWorkflowState(
      submissionUuids: string[],
      opts?: {
        requests?: Request[]
        baseMutations?: Record<string, SubjectMutation>
        commit?: boolean
      }
    ): Promise<Record<string, SubjectMutation>> {
      const unique = [...new Set(submissionUuids.filter(Boolean))]
      const requests = opts?.requests ?? this.ingestedRequests
      let next: Record<string, SubjectMutation> = {
        ...(opts?.baseMutations ?? this.subjectMutations),
      }
      if (unique.length === 0) {
        if (opts?.commit !== false) this.subjectMutations = next
        return next
      }
      const lines = await fetchDdQueueState(unique)
      const bySubmission = new Map<string, string>()
      for (const req of requests) {
        if (req.submissionUuid) bySubmission.set(req.submissionUuid, req.id)
      }

      const submissionModes = new Map<string, EstimationMode | null>()
      for (const snap of lines) {
        if (isEmptyWorkflowSnapshot(snap)) continue
        const requestId = bySubmission.get(snap.submission_uuid)
        if (!requestId) continue
        const key = `${requestId}:${snap.line_key}`
        next = {
          ...next,
          [key]: workflowSnapshotToMutationPatch(snap, next[key], { replaceChat: true }),
        }
        if (snap.submission_estimation_mode !== undefined) {
          const mode = snap.submission_estimation_mode
          submissionModes.set(
            requestId,
            mode && mode.length > 0 ? (mode as EstimationMode) : null,
          )
        }
      }
      if (opts?.commit !== false) {
        this.subjectMutations = next
        let nextRequests = applyLineOverlaysToRequests(this.ingestedRequests, next)
        for (const [requestId, mode] of submissionModes) {
          nextRequests = nextRequests.map((r) =>
            r.id === requestId ? { ...r, submissionEstimationMode: mode } : r
          )
        }
        this.ingestedRequests = nextRequests
      } else if (opts?.requests && submissionModes.size > 0) {
        // commit:false — patch caller's requests in place so loadQueueFromServer
        // atomic reveal keeps submissionEstimationMode across reload.
        for (let i = 0; i < opts.requests.length; i++) {
          const r = opts.requests[i]
          if (!r || !submissionModes.has(r.id)) continue
          opts.requests[i] = {
            ...r,
            submissionEstimationMode: submissionModes.get(r.id) ?? null,
          }
        }
      }
      return next
    },

    applyWorkflowLineSnapshot(
      requestId: string,
      sku: string,
      snapshot: DdWorkflowLineSnapshot
    ) {
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      let nextMutations = {
        ...this.subjectMutations,
        [key]: workflowSnapshotToMutationPatch(snapshot, existing),
      }
      // NP-ROUTING-FREEZE-FIX-001 — re-assert sibling mut from submission SoT after complete.
      const submissionMode = snapshot.submission_estimation_mode
      if (submissionMode && submissionMode.length > 0) {
        const mode = submissionMode as EstimationMode
        const req = this.findRequest(requestId)
        if (req?.isMaster) {
          for (const child of req.children) {
            const siblingKey = `${requestId}:${child.sku}`
            nextMutations = {
              ...nextMutations,
              [siblingKey]: {
                ...(nextMutations[siblingKey] ?? {}),
                estimationMode: mode,
              },
            }
          }
        } else if (req && !req.isMaster) {
          const singleKey = `${requestId}:${(req as Single).sku}`
          nextMutations = {
            ...nextMutations,
            [singleKey]: {
              ...(nextMutations[singleKey] ?? {}),
              estimationMode: mode,
            },
          }
        }
      }
      this.subjectMutations = nextMutations
      let nextRequests = applyLineOverlaysToRequests(this.ingestedRequests, nextMutations)
      if (snapshot.submission_estimation_mode !== undefined) {
        const mode = snapshot.submission_estimation_mode
        nextRequests = nextRequests.map((r) =>
          r.id === requestId
            ? {
                ...r,
                submissionEstimationMode:
                  mode && mode.length > 0 ? (mode as EstimationMode) : null,
              }
            : r
        )
      }
      this.ingestedRequests = nextRequests
    },

    /**
     * NP-TICKET-PER-SUBMISSION-001 — silent master/request-level routing.
     * Optimistic fan-out; restore siblings + submissionEstimationMode on API failure.
     */
    async setSubmissionEstimationMode(requestId: string, estimationMode: EstimationMode) {
      const req = this.findRequest(requestId)
      if (!req?.submissionUuid) return

      const priorMutations = { ...this.subjectMutations }
      const priorRequests = this.ingestedRequests

      this.ingestedRequests = this.ingestedRequests.map((r) =>
        r.id === requestId ? { ...r, submissionEstimationMode: estimationMode } : r
      )
      if (req.isMaster) {
        for (const child of req.children) {
          const key = `${requestId}:${child.sku}`
          this.subjectMutations = {
            ...this.subjectMutations,
            [key]: { ...(this.subjectMutations[key] ?? {}), estimationMode },
          }
        }
      } else {
        const single = req as Single
        const key = `${requestId}:${single.sku}`
        this.subjectMutations = {
          ...this.subjectMutations,
          [key]: { ...(this.subjectMutations[key] ?? {}), estimationMode },
        }
      }

      try {
        await putDdWorkflowEstimationMode({
          submissionUuid: req.submissionUuid,
          estimationMode,
        })
      } catch (err) {
        this.subjectMutations = priorMutations
        this.ingestedRequests = priorRequests
        toastWorkflowError(err instanceof Error ? err.message : 'Failed to set routing')
      }
    },

    /**
     * ROUTING-E1 — persist PIQ estimate fields on the line (packet PUT).
     */
    async savePiqEstimate(
      requestId: string,
      sku: string,
      fields: {
        quoteNumber: string | null
        unitPriceCents: number | null
        taxRate: number | null
        paymentTerms: string | null
        quoteUrl: string | null
      }
    ) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const existing = this.subjectMutations[key] ?? {}
      const prior = this.subjectMutations[key]
      const priorIngested = this.ingestedRequests
      const expectedVersion = existing.workflowLineVersion ?? null
      const nextMutation: SubjectMutation = {
        ...existing,
        quoteNumber: fields.quoteNumber ?? undefined,
        unitPriceCents: fields.unitPriceCents,
        taxRate: fields.taxRate,
        paymentTerms: fields.paymentTerms ?? undefined,
        quoteUrl: fields.quoteUrl ?? undefined,
        activityHead: [
          { t: 'just now', e: `${me()} updated PIQ estimate` },
          ...(existing.activityHead ?? []),
        ],
      }
      if (!fields.quoteNumber) delete nextMutation.quoteNumber
      if (!fields.paymentTerms) delete nextMutation.paymentTerms
      if (!fields.quoteUrl) delete nextMutation.quoteUrl

      this.subjectMutations = { ...this.subjectMutations, [key]: nextMutation }
      this.ingestedRequests = applyLineOverlaysToRequests(
        this.ingestedRequests,
        this.subjectMutations
      )

      if (!req?.submissionUuid) return

      try {
        const snapshot = await putDdWorkflowPacket({
          submissionUuid: req.submissionUuid,
          lineKey: sku,
          expectedVersion,
          quoteNumber: fields.quoteNumber,
          unitPriceCents: fields.unitPriceCents,
          taxRate: fields.taxRate,
          paymentTerms: fields.paymentTerms,
          quoteUrl: fields.quoteUrl,
        })
        this.applyWorkflowLineSnapshot(requestId, sku, snapshot)
      } catch (err) {
        if (err instanceof DdWorkflowConflictError) {
          toastWorkflowError('Someone else updated this step')
          this.applyWorkflowLineSnapshot(requestId, sku, err.snapshot)
          return
        }
        if (prior === undefined) {
          const { [key]: _removed, ...rest } = this.subjectMutations
          this.subjectMutations = rest
        } else {
          this.subjectMutations = { ...this.subjectMutations, [key]: prior }
        }
        this.ingestedRequests = priorIngested
        toastWorkflowError(err instanceof Error ? err.message : 'Workflow update failed')
      }
    },

    async uploadPiqQuoteFile(requestId: string, sku: string, file: File): Promise<void> {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      if (!req?.submissionUuid) return
      const existing = this.subjectMutations[key] ?? {}
      const priorQuote = existing.quoteFile ?? null

      try {
        const uploaded = await uploadDesignDevelopmentQueueFile({
          submissionUuid: req.submissionUuid,
          file,
          lineKey: sku,
          kind: 'quote',
        })
        const quoteFile: DesignFile = {
          id: uploaded.uuid,
          name: uploaded.original_name,
          type: uploaded.mime ?? 'application/octet-stream',
          size: uploaded.size,
          downloadUrl: uploaded.download_url,
          uploadedBy: uploaded.uploaded_by_name ?? me(),
          t: uploaded.uploaded_at ?? '',
        }
        const parse = uploaded.piqParse
        const next: SubjectMutation = {
          ...existing,
          quoteFile,
          activityHead: [
            {
              t: 'just now',
              e: `${me()} uploaded a PIQ quote — ${uploaded.original_name}`,
            },
            ...(existing.activityHead ?? []),
          ],
        }
        // HS-PARSE-CR-001 — when parse payload is present, explicit null CLEARS prior fields.
        if (parse) {
          next.quoteNumber = parse.quoteNumber ?? null
          next.unitPriceCents = parse.unitPriceCents ?? null
          next.piqQuoteStatus = parse.piqQuoteStatus ?? null
          next.piqUnitPriceSource = parse.piqUnitPriceSource ?? null
        }
        this.subjectMutations = {
          ...this.subjectMutations,
          [key]: next,
        }
        this.ingestedRequests = applyLineOverlaysToRequests(
          this.ingestedRequests,
          this.subjectMutations
        )
      } catch (err) {
        this.subjectMutations = {
          ...this.subjectMutations,
          [key]: { ...existing, quoteFile: priorQuote },
        }
        toastWorkflowError(err instanceof Error ? err.message : 'Quote upload failed')
      }
    },

    async clearPiqQuoteFile(requestId: string, sku: string): Promise<void> {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const existing = this.subjectMutations[key] ?? {}
      const prior = existing.quoteFile
      if (!prior?.id || !req?.submissionUuid) {
        this.subjectMutations = {
          ...this.subjectMutations,
          [key]: { ...existing, quoteFile: null },
        }
        return
      }
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: { ...existing, quoteFile: null },
      }
      try {
        await deleteDesignDevelopmentQueueFile(req.submissionUuid, prior.id)
      } catch (err) {
        this.subjectMutations = {
          ...this.subjectMutations,
          [key]: { ...existing, quoteFile: prior },
        }
        toastWorkflowError(err instanceof Error ? err.message : 'Failed to remove quote file')
      }
    },

    async persistWorkflowMutation(
      requestId: string,
      sku: string,
      prior: SubjectMutation | undefined,
      run: () => Promise<DdWorkflowLineSnapshot>,
      rollback?: {
        subjectMutations: Record<string, SubjectMutation>
        ingestedRequests: Request[]
      }
    ): Promise<void> {
      const req = this.findRequest(requestId)
      if (!req?.submissionUuid) return
      try {
        const snapshot = await run()
        this.applyWorkflowLineSnapshot(requestId, sku, snapshot)
      } catch (err) {
        if (err instanceof DdWorkflowConflictError) {
          toastWorkflowError('Someone else updated this step')
          this.applyWorkflowLineSnapshot(requestId, sku, err.snapshot)
          return
        }
        if (rollback) {
          this.subjectMutations = rollback.subjectMutations
          this.ingestedRequests = rollback.ingestedRequests
        } else {
          const key = `${requestId}:${sku}`
          if (prior === undefined) {
            const { [key]: _removed, ...rest } = this.subjectMutations
            this.subjectMutations = rest
          } else {
            this.subjectMutations = { ...this.subjectMutations, [key]: prior }
          }
          this.ingestedRequests = applyLineOverlaysToRequests(
            this.ingestedRequests,
            this.subjectMutations
          )
        }
        toastWorkflowError(err instanceof Error ? err.message : 'Workflow update failed')
      }
    },

    setPeekId(id: string | null, childSku: string | null = null) {
      this.peekId = id
      this.peekChildSku = childSku
    },

    setPeekChildSku(sku: string | null) {
      this.peekChildSku = sku
    },

    setFullRequest(id: string | null, sku: string | null = null) {
      this.fullRequestId = id
      this.fullRequestSku = sku
    },

    setQuoteRequest(id: string | null) {
      this.quoteRequestId = id
    },

    toggleMasterExpanded(id: string) {
      const next = { ...this.expandedMasters }
      if (next[id]) delete next[id]
      else next[id] = true
      this.expandedMasters = next
    },

    setSort(col: string) {
      if (this.sort?.col === col) {
        this.sort =
          this.sort.dir === 'asc' ? { col, dir: 'desc' } : null
      } else {
        this.sort = { col, dir: 'asc' }
      }
    },

    setRole(role: string) {
      this.role = role
    },

    setScope(scope: string) {
      this.scope = scope
    },

    setFilter(key: keyof FilterState, value: string) {
      this.filters = { ...this.filters, [key]: value }
    },

    setSearch(search: string) {
      this.search = search
    },

    clearFilters() {
      this.filters = { ...EMPTY_FILTERS }
      this.search = ''
    },

    clearIngestedRequests() {
      this.ingestedRequests = []
      this.subjectMutations = {}
      this.peekId = null
      this.peekChildSku = null
      this.fullRequestId = null
      this.fullRequestSku = null
      this.quoteRequestId = null
      this.expandedMasters = {}
      this.selectedBulk = {}
      this.lastBulkClickIndex = null
      this.intakeLoaded = false
      this.intakeMeta = null
      this.intakeError = null
      intakeLoadInFlight = null
    },

    loadFixtureRequests(requests: Request[]) {
      this.ingestedRequests = [...requests]
      // Fixtures are a complete in-memory dataset — treat as settled for empty-state gates.
      this.intakeLoaded = true
      this.intakeLoading = false
      this.intakeError = null
    },

    findRequest(id: string): Request | undefined {
      return this.ingestedRequests.find((r) => r.id === id)
    },

    toggleBulkSelected(id: string) {
      const next = { ...this.selectedBulk }
      if (next[id]) delete next[id]
      else next[id] = true
      this.selectedBulk = next
    },

    setBulkSelectedMany(keys: string[], selected: boolean) {
      const next = { ...this.selectedBulk }
      for (const k of keys) {
        if (selected) next[k] = true
        else delete next[k]
      }
      this.selectedBulk = next
    },

    clearBulkSelected() {
      this.selectedBulk = {}
      this.lastBulkClickIndex = null
    },

    selectAllBulk(ids: string[]) {
      const next = { ...this.selectedBulk }
      for (const id of ids) next[id] = true
      this.selectedBulk = next
    },

    setLastBulkClickIndex(index: number | null) {
      this.lastBulkClickIndex = index
    },

    rangeBulkSelect(fromIndex: number, toIndex: number, allIds: string[]) {
      const next = { ...this.selectedBulk }
      const lo = Math.min(fromIndex, toIndex)
      const hi = Math.max(fromIndex, toIndex)
      for (let i = lo; i <= hi; i++) {
        const id = allIds[i]
        if (!id) continue
        const req = this.findRequest(id)
        if (req?.isMaster) {
          for (const c of req.children) next[`${id}:${c.sku}`] = true
        } else {
          next[id] = true
        }
      }
      this.selectedBulk = next
      this.lastBulkClickIndex = toIndex
    },

    setColumnOrder(order: string[]) {
      this.columnOrder = order
    },

    reorderColumn(fromId: string, toId: string) {
      const oldIndex = this.columnOrder.indexOf(fromId)
      const newIndex = this.columnOrder.indexOf(toId)
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return
      this.columnOrder = arrayMove(this.columnOrder, oldIndex, newIndex)
    },

    setColumnWidth(id: string, width: number) {
      this.columnWidths = { ...this.columnWidths, [id]: width }
    },

    toggleColumnVisible(id: string) {
      const next = { ...this.hiddenColumns }
      if (next[id]) delete next[id]
      else next[id] = true
      this.hiddenColumns = next
    },

    setHistorySort(col: string) {
      if (this.historySort?.col === col) {
        this.historySort =
          this.historySort.dir === 'asc' ? { col, dir: 'desc' } : null
      } else {
        this.historySort = { col, dir: 'asc' }
      }
    },

    setHistoryColumnOrder(order: string[]) {
      this.historyColumnOrder = order
    },

    reorderHistoryColumn(fromId: string, toId: string) {
      const oldIndex = this.historyColumnOrder.indexOf(fromId)
      const newIndex = this.historyColumnOrder.indexOf(toId)
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return
      this.historyColumnOrder = arrayMove(this.historyColumnOrder, oldIndex, newIndex)
    },

    setHistoryColumnWidth(id: string, width: number) {
      this.historyColumnWidths = { ...this.historyColumnWidths, [id]: width }
    },

    toggleHistoryColumnVisible(id: string) {
      const next = { ...this.historyHiddenColumns }
      if (next[id]) delete next[id]
      else next[id] = true
      this.historyHiddenColumns = next
    },

    async restoreSubject(requestId: string, sku: string) {
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key]
      if (!existing?.deletedAt) return

      const prior = existing
      const { deletedAt: _da, deletedBy: _db, deletedReason: _dr, ...rest } = existing
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...rest,
          activityHead: [
            { t: 'just now', e: `${me()} restored this line to the queue` },
            ...(existing.activityHead ?? []),
          ],
        },
      }

      const req = this.findRequest(requestId)
      if (!req?.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        postDdWorkflowRestore({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          expectedVersion: prior.workflowLineVersion ?? null,
        })
      )
    },

    pinCurrentView() {
      const pinned: DdPinnedView = {
        filters: { ...this.filters },
        search: this.search,
        role: this.role,
        scope: this.scope,
        columnOrder: [...this.columnOrder],
        columnWidths: { ...this.columnWidths },
        hiddenColumns: { ...this.hiddenColumns },
      }
      saveDdPinnedView(pinned)
      this.pinnedView = pinned
    },

    unpinView() {
      clearDdPinnedView()
      this.pinnedView = null
    },

    restorePinnedView() {
      if (!this.pinnedView) return
      this.filters = { ...this.pinnedView.filters }
      this.search = this.pinnedView.search
      this.role = this.pinnedView.role
      this.scope = this.pinnedView.scope
      this.columnOrder = [...this.pinnedView.columnOrder]
      this.columnWidths = { ...this.pinnedView.columnWidths }
      this.hiddenColumns = { ...this.pinnedView.hiddenColumns }
    },

    async unassignDeliverable(requestId: string, sku: string, stepKey: string) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const stepDef = stepByKey(stepKey)
      if (stepDef?.unassignable) return

      const existing = this.subjectMutations[key] ?? {}
      const resolved = req ? resolveSubject(req, sku, this.subjectMutations) : null
      const prev = resolved?.deliverables[stepKey]?.assignee ?? null
      if (!prev) return

      const prior = this.subjectMutations[key]
      const currentStatus = resolved?.deliverables[stepKey]?.status ?? 'pending'
      const stepName = stepDef?.name ?? stepKey
      const expectedVersion = existing.workflowStepVersions?.[stepKey] ?? null

      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          deliverableUpdates: {
            ...(existing.deliverableUpdates ?? {}),
            [stepKey]: { status: currentStatus, assignee: null },
          },
          activityHead: [
            { t: 'just now', e: `${me()} unassigned ${stepName} (was ${prev})` },
            ...(existing.activityHead ?? []),
          ],
        },
      }

      if (!req?.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        putDdWorkflowAssignment({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          stepKey,
          assignee: null,
          expectedVersion,
        })
      )
    },

    async assignDeliverable(
      requestId: string,
      sku: string,
      stepKey: string,
      operator: string
    ) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const stepDef = stepByKey(stepKey)
      if (stepDef?.unassignable) return

      const existing = this.subjectMutations[key] ?? {}
      const prior = this.subjectMutations[key]
      const resolved = req ? resolveSubject(req, sku, this.subjectMutations) : null
      const prev = resolved?.deliverables[stepKey]?.assignee ?? null
      const currentStatus = resolved?.deliverables[stepKey]?.status ?? 'pending'
      const isOpen = currentStatus === 'open'
      const stepName = stepDef?.name ?? stepKey
      const expectedVersion = existing.workflowStepVersions?.[stepKey] ?? null

      const entry = {
        t: 'just now',
        e:
          prev && prev !== operator
            ? `${me()} reassigned ${stepName} from ${prev} to ${operator}`
            : `${me()} assigned ${operator} to ${stepName}`,
      }

      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          deliverableUpdates: {
            ...(existing.deliverableUpdates ?? {}),
            [stepKey]: { status: currentStatus, assignee: operator },
          },
          deliverableOpenedAt:
            isOpen && existing.deliverableOpenedAt?.[stepKey] == null
              ? {
                  ...(existing.deliverableOpenedAt ?? {}),
                  [stepKey]: Date.now(),
                }
              : existing.deliverableOpenedAt,
          activityHead: [entry, ...(existing.activityHead ?? [])],
        },
      }

      if (!req?.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        putDdWorkflowAssignment({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          stepKey,
          assignee: operator,
          expectedVersion,
        })
      )
    },

    async deleteSubject(requestId: string, sku: string, reason: string) {
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      if (existing.deletedAt) return

      const prior = this.subjectMutations[key]
      const expectedVersion = existing.workflowLineVersion ?? null

      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          deletedAt: 'just now',
          deletedBy: me(),
          ...(reason ? { deletedReason: reason } : {}),
          activityHead: [
            {
              t: 'just now',
              e: `${me()} deleted this line${reason ? ` — ${reason}` : ''}`,
            },
            ...(existing.activityHead ?? []),
          ],
        },
      }

      const req = this.findRequest(requestId)
      if (!req?.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        postDdWorkflowCancel({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          reason,
          expectedVersion,
        })
      )
    },

    /**
     * BUG-STG-006-FIX-2 — Edit Line save: optimistic Pinia + PUT …/editor.
     * Uses persistWorkflowMutation (rollback + toast on failure).
     */
    async saveLineEditor(
      requestId: string,
      sku: string,
      args: { nameOverride: string; attributes: Record<string, string> }
    ): Promise<boolean> {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const prior = this.subjectMutations[key]
      const existing = prior ?? {}
      const trimmedName = args.nameOverride.trim()
      const merged: Record<string, string> = {}
      for (const [k, v] of Object.entries(args.attributes)) {
        if (v && v.trim()) merged[k] = v.trim()
      }

      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          nameOverride: trimmedName || undefined,
          attributes: merged,
          activityHead: [
            { t: 'just now', e: `${me()} edited attributes` },
            ...(trimmedName !== (existing.nameOverride ?? '')
              ? [{ t: 'just now', e: `${me()} updated description` } as const]
              : []),
            ...(existing.activityHead ?? []),
          ],
        },
      }

      if (!req?.submissionUuid) {
        toastWorkflowError('Cannot save — line is not linked to a submission')
        if (prior === undefined) {
          const { [key]: _removed, ...rest } = this.subjectMutations
          this.subjectMutations = rest
        } else {
          this.subjectMutations = { ...this.subjectMutations, [key]: prior }
        }
        return false
      }

      const expectedVersion = existing.workflowLineVersion ?? null
      try {
        const snapshot = await putDdWorkflowLineEditor({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          expectedVersion,
          nameOverride: trimmedName || null,
          lineAttributes: merged,
        })
        this.applyWorkflowLineSnapshot(requestId, sku, snapshot)
        return true
      } catch (err) {
        if (err instanceof DdWorkflowConflictError) {
          toastWorkflowError('Someone else updated this line')
          this.applyWorkflowLineSnapshot(requestId, sku, err.snapshot)
          return false
        }
        if (prior === undefined) {
          const { [key]: _removed, ...rest } = this.subjectMutations
          this.subjectMutations = rest
        } else {
          this.subjectMutations = { ...this.subjectMutations, [key]: prior }
        }
        toastWorkflowError(err instanceof Error ? err.message : 'Line editor save failed')
        return false
      }
    },

    setPermit(permit: 'supervisor' | 'user') {
      this.permit = permit
    },

    syncPermitFromAuth(hasPermission: HasPermissionFn) {
      this.permit = resolveDdPermitFromPermissions(hasPermission)
    },

    async completeDeliverable(
      requestId: string,
      sku: string,
      stepKey: string,
      opts?: { force?: boolean }
    ) {
      const force = opts?.force === true
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      if (!req) {
        toastWorkflowError('Could not complete — request not found in queue')
        return
      }

      const existing = this.subjectMutations[key] ?? {}
      const prior = this.subjectMutations[key]
      const expectedVersion = existing.workflowStepVersions?.[stepKey] ?? null
      const resolved = resolveSubject(req, sku, this.subjectMutations)
      if (!resolved) {
        toastWorkflowError('Could not complete — line not found on request')
        return
      }

      let newDeliverables = advanceDeliverable(
        resolved.deliverables,
        stepKey,
        { estimationMode: resolved.estimationMode }
      )
      newDeliverables = autoCompleteOpen(newDeliverables, resolved.estimationMode)

      const completedEntry = newDeliverables[stepKey]
      if (force && stepKey === 'estimation' && completedEntry) {
        // Match applyEstimationUpdate / server force-complete: clear assignee.
        newDeliverables = {
          ...newDeliverables,
          [stepKey]: { ...completedEntry, assignee: null },
        }
      } else if (
        completedEntry &&
        (!completedEntry.assignee || completedEntry.assignee === '__unassigned__')
      ) {
        newDeliverables = {
          ...newDeliverables,
          [stepKey]: { ...completedEntry, assignee: me() },
        }
      }

      const seedSubject = req.isMaster
        ? req.children.find((c) => c.sku === sku)
        : req
      const deltaUpdates: Record<string, Partial<DeliverableEntry>> = {}
      if (seedSubject) {
        for (const [k, entry] of Object.entries(newDeliverables)) {
          const seed = seedSubject.deliverables[k]
          if (seed?.status !== entry.status || seed?.assignee !== entry.assignee) {
            deltaUpdates[k] = entry
          }
        }
      }

      const now = Date.now()
      // Server folds running STEP TIMER on complete (PR2b-3) — do not auto-stop client-side.
      const timerMs = existing.designTimerMs ?? resolved.designTimerMs ?? 0
      const timerStartedAt = existing.designTimerStartedAt ?? null

      const openedAtStamp: Record<string, number> = {
        ...(existing.deliverableOpenedAt ?? {}),
      }
      for (const [k, entry] of Object.entries(newDeliverables)) {
        if (entry.status === 'open' && openedAtStamp[k] == null) {
          openedAtStamp[k] = now
        }
      }

      let quotedProofId = existing.quotedProofId
      // Design proof mint is server SoT (design_development_queue_files.proof_version).
      // Send draft uuid; hydrateSubjectFiles after persist refreshes version numbers.
      if (stepKey === 'design') {
        const pvs = existing.proofVersions ?? []
        for (let i = pvs.length - 1; i >= 0; i--) {
          if (!pvs[i].deletedAt && pvs[i].version === 0) {
            quotedProofId = pvs[i].id
            break
          }
        }
      }

      const stepLabel = stepKey.replace(/_/g, ' ')
      const activityText = force
        ? `${me()} force-completed ${stepLabel}${req.isMaster ? ' on ' + sku : ''}`
        : `${me()} completed ${stepLabel}${req.isMaster ? ' on ' + sku : ''}`

      // DD-NOW-C-001 / 8435b8b: Design Job + design_first HubSpot handoff placeholder (server SoT for live lines).
      // HS-TICKET-TIMING-002 — queued activity comes from server snapshot for NEW Product.
      const designFirstActivity: { t: string; e: string }[] = []

      const nextStepTimers = { ...(existing.stepTimers ?? {}) }
      if (stepKey === 'design' || existing.stepTimers?.design) {
        nextStepTimers.design = {
          ms: timerMs,
          startedAt: timerStartedAt,
        }
      }

      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          deliverableUpdates: {
            ...(existing.deliverableUpdates ?? {}),
            ...deltaUpdates,
          },
          deliverableCompletedAt: {
            ...(existing.deliverableCompletedAt ?? {}),
            [stepKey]: now,
          },
          deliverableOpenedAt: openedAtStamp,
          designTimerMs: timerMs,
          designTimerStartedAt: timerStartedAt,
          stepTimers: nextStepTimers,
          ...(quotedProofId ? { quotedProofId } : {}),
          activityHead: [
            ...designFirstActivity,
            {
              t: 'just now',
              e: activityText,
            },
            ...(existing.activityHead ?? []),
          ],
        },
      }

      if (!req.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        postDdWorkflowComplete({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          stepKey,
          expectedVersion,
          ...(force ? { force: true } : {}),
          ...(stepKey === 'design' && quotedProofId ? { quotedProofId } : {}),
          ...(stepKey === 'pm_assessment' && resolved.estimationMode
            ? { estimationMode: resolved.estimationMode }
            : {}),
        })
      )
      if (stepKey === 'design' && req.submissionUuid) {
        await this.hydrateSubjectFiles(requestId, sku).catch(() => {
          /* non-fatal — quoted_proof_id already applied from snapshot */
        })
      }
    },

    async startDesignTimer(requestId: string, sku: string) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const existing = this.subjectMutations[key] ?? {}
      const prior = this.subjectMutations[key]
      const expectedVersion = existing.workflowStepVersions?.design ?? null
      const designTimer = existing.stepTimers?.design
      const alreadyRunning =
        (designTimer?.startedAt ?? existing.designTimerStartedAt ?? null) != null
      if (alreadyRunning) return

      const startedAt = Date.now()
      const ms = designTimer?.ms ?? existing.designTimerMs ?? 0
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          designTimerStartedAt: startedAt,
          designTimerMs: ms,
          stepTimers: {
            ...(existing.stepTimers ?? {}),
            design: { ms, startedAt },
          },
        },
      }

      if (!req?.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        postDdWorkflowTimerStart({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          stepKey: 'design',
          expectedVersion,
        })
      )
    },

    async reactivateStep(requestId: string, sku: string, stepKey: string) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const stepName = stepByKey(stepKey)?.name ?? stepKey
      const existing = this.subjectMutations[key] ?? {}
      const prior = this.subjectMutations[key]
      const expectedVersion = existing.workflowStepVersions?.[stepKey] ?? null
      const resolved = req ? resolveSubject(req, sku, this.subjectMutations) : null
      if (resolved?.deliverables[stepKey]?.status !== 'complete') return

      const plan = buildStepReactivation(
        resolved.deliverables,
        stepKey,
        resolved.estimationMode
      )
      const now = Date.now()

      const completedAt = { ...(existing.deliverableCompletedAt ?? {}) }
      for (const k of plan.clearCompletedAt) {
        delete completedAt[k]
      }

      const openedAt = { ...(existing.deliverableOpenedAt ?? {}) }
      for (const k of plan.clearOpenedAt) {
        delete openedAt[k]
      }
      openedAt[stepKey] = now

      const nextMutation: SubjectMutation = {
        ...existing,
        deliverableUpdates: {
          ...(existing.deliverableUpdates ?? {}),
          ...plan.deliverableUpdates,
        },
        deliverableOpenedAt: openedAt,
        deliverableCompletedAt: completedAt,
        activityHead: [
          {
            t: 'just now',
            e: `${me()} reactivated ${stepName} — needs to be re-worked`,
          },
          ...(existing.activityHead ?? []),
        ],
      }
      if (plan.clearQuotedProofId) {
        delete nextMutation.quotedProofId
      }

      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: nextMutation,
      }

      // Server is SoT for reactivation; success replaces overlay with snapshot.
      if (!req?.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        postDdWorkflowReactivate({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          stepKey,
          expectedVersion,
        })
      )
    },

    async stopDesignTimer(requestId: string, sku: string) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const existing = this.subjectMutations[key] ?? {}
      const prior = this.subjectMutations[key]
      const expectedVersion = existing.workflowStepVersions?.design ?? null
      const designTimer = existing.stepTimers?.design
      const startedAt = designTimer?.startedAt ?? existing.designTimerStartedAt ?? null
      if (startedAt == null) return
      const sessionMs = Date.now() - startedAt
      const resolved = req ? resolveSubject(req, sku, this.subjectMutations) : null
      const totalMs =
        (designTimer?.ms ?? existing.designTimerMs ?? resolved?.designTimerMs ?? 0) + sessionMs
      const mins = Math.round(sessionMs / 60000)
      const sessionStr =
        mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          designTimerMs: totalMs,
          designTimerStartedAt: null,
          stepTimers: {
            ...(existing.stepTimers ?? {}),
            design: { ms: totalMs, startedAt: null },
          },
          activityHead: [
            { t: 'just now', e: `${me()} logged ${sessionStr} on Design` },
            ...(existing.activityHead ?? []),
          ],
        },
      }

      if (!req?.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        postDdWorkflowTimerStop({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          stepKey: 'design',
          expectedVersion,
        })
      )
    },

    async completePMAssessment(
      requestId: string,
      sku: string,
      deliverableOverrides: Record<string, boolean>,
      attributes: Record<string, string>,
      modes?: Partial<{
        estimationMode: SubjectMutation['estimationMode']
        pimMode: SubjectMutation['pimMode']
        installRequired: boolean
      }>
    ) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      if (!req) return

      const existing = this.subjectMutations[key] ?? {}
      const prior = this.subjectMutations[key]
      const priorFanOut = {
        subjectMutations: { ...this.subjectMutations },
        ingestedRequests: this.ingestedRequests,
      }
      const expectedVersion = existing.workflowStepVersions?.pm_assessment ?? null
      const resolved = resolveSubject(req, sku, this.subjectMutations)
      if (!resolved) return

      const estimationMode = modes?.estimationMode ?? resolved.estimationMode
      const baseDeliverables: Record<string, DeliverableEntry> = {}
      for (const [k, required] of Object.entries(deliverableOverrides)) {
        baseDeliverables[k] = { status: required ? 'pending' : 'not_required', assignee: null }
      }

      let newDeliverables = advanceDeliverable(
        { ...resolved.deliverables, ...baseDeliverables },
        'pm_assessment',
        { estimationMode }
      )
      newDeliverables = autoCompleteOpen(newDeliverables, estimationMode)

      const seedSubject = req.isMaster
        ? req.children.find((c) => c.sku === sku)
        : req
      const deltaUpdates: Record<string, Partial<DeliverableEntry>> = {}
      if (seedSubject) {
        for (const [k, entry] of Object.entries(newDeliverables)) {
          const seed = seedSubject.deliverables[k]
          if (seed?.status !== entry.status || seed?.assignee !== entry.assignee) {
            deltaUpdates[k] = entry
          }
        }
      }

      this.fullRequestId = null
      this.fullRequestSku = null
      let nextMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          deliverableUpdates: { ...(existing.deliverableUpdates ?? {}), ...deltaUpdates },
          deliverableOpenedAt: stampOpenedAt(
            resolved.deliverables,
            newDeliverables,
            existing.deliverableOpenedAt
          ),
          estimationMode: modes?.estimationMode ?? existing.estimationMode,
          pimMode: modes?.pimMode ?? existing.pimMode,
          installRequired: modes?.installRequired ?? existing.installRequired,
          attributes: { ...(existing.attributes ?? {}), ...attributes },
          activityHead: [
            { t: 'just now', e: `${me()} completed Triage` },
            ...(existing.activityHead ?? []),
          ],
        },
      }
      // D1 — fan-out estimation mode to all sibling skus locally.
      if (req.isMaster && estimationMode) {
        for (const child of req.children) {
          const siblingKey = `${requestId}:${child.sku}`
          nextMutations = {
            ...nextMutations,
            [siblingKey]: {
              ...(nextMutations[siblingKey] ?? {}),
              estimationMode,
            },
          }
        }
      }
      this.subjectMutations = nextMutations
      this.ingestedRequests = this.ingestedRequests.map((r) =>
        r.id === requestId ? { ...r, submissionEstimationMode: estimationMode } : r
      )

      if (!req.submissionUuid) return
      await this.persistWorkflowMutation(
        requestId,
        sku,
        prior,
        () =>
          postDdWorkflowComplete({
            submissionUuid: req.submissionUuid!,
            lineKey: sku,
            stepKey: 'pm_assessment',
            expectedVersion,
            estimationMode: estimationMode ?? null,
            installRequired: modes?.installRequired ?? resolved.installRequired,
            deliverables: deliverableOverrides,
          }),
        priorFanOut
      )
    },

    convertModifyToNew(requestId: string, sku: string) {
      let converted = false
      this.ingestedRequests = this.ingestedRequests.map((r) => {
        if (r.id !== requestId || r.isMaster || r.sku !== sku || !r.modify) return r
        converted = true
        const { modify: _drop, ...rest } = r
        return {
          ...rest,
          path: 'new' as const,
          productType: 'custom' as const,
          estimationMode: 'standard' as const,
          pimMode: 'pim' as const,
          deliverables: {
            ...r.deliverables,
            pm_assessment: { status: 'open' as const, assignee: null },
          },
          flags: (r.flags ?? []).filter((f) => !/PM Review needed/i.test(f)),
        }
      })
      if (!converted) return

      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          deliverableUpdates: {},
          activityHead: [
            { t: 'just now', e: `${me()} reclassified this change request as a NEW product` },
            ...(existing.activityHead ?? []),
          ],
        },
      }
    },

    async postChatMessage(
      requestId: string,
      sku: string,
      text: string,
      mentions: string[] = [],
      author?: string,
      clientId?: string
    ) {
      const trimmed = text.trim()
      if (!trimmed) return
      const by = author ?? me()
      const key = `${requestId}:${sku}`
      const prior = this.subjectMutations[key]
      const existing = prior ?? {}
      const req = this.findRequest(requestId)
      const id =
        clientId ??
        (req?.submissionUuid && typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : genChatId())
      if ((existing.chat ?? []).some((m) => m.id === id)) return

      const msg: ChatMessage = {
        id,
        author: by,
        text: trimmed,
        t: 'just now',
        ts: Date.now(),
        ...(mentions.length ? { mentions } : {}),
      }
      const mentionNote = mentions.length ? ` (mentioned: ${mentions.join(', ')})` : ''
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          chat: [...(existing.chat ?? []), msg],
          activityHead: [
            { t: 'just now', e: `${by} posted a note${mentionNote}` },
            ...(existing.activityHead ?? []),
          ],
        },
      }

      if (!req?.submissionUuid) return

      try {
        const snap = await postDdWorkflowChat({
          submissionUuid: req.submissionUuid,
          lineKey: sku,
          id,
          text: trimmed,
          mentions: mentions.length ? mentions : undefined,
        })
        const cur = this.subjectMutations[key] ?? {}
        const serverMsg = chatMessageFromSnapshot(snap)
        this.subjectMutations = {
          ...this.subjectMutations,
          [key]: {
            ...cur,
            chat: (cur.chat ?? []).map((m) => (m.id === id ? serverMsg : m)),
          },
        }
      } catch (err) {
        if (prior === undefined) {
          const { [key]: _removed, ...rest } = this.subjectMutations
          this.subjectMutations = rest
        } else {
          this.subjectMutations = { ...this.subjectMutations, [key]: prior }
        }
        toastWorkflowError(err instanceof Error ? err.message : 'Chat post failed')
      }
    },

    async postChatReply(
      requestId: string,
      sku: string,
      parentId: string,
      text: string,
      mentions: string[] = [],
      author?: string,
      clientId?: string
    ) {
      const trimmed = text.trim()
      if (!trimmed) return
      const by = author ?? me()
      const key = `${requestId}:${sku}`
      const prior = this.subjectMutations[key]
      const existing = prior ?? {}
      const byParent = existing.chatReplies ?? {}
      const req = this.findRequest(requestId)
      const id =
        clientId ??
        (req?.submissionUuid && typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : genChatId())
      if ((byParent[parentId] ?? []).some((r) => r.id === id)) return

      const reply: ChatMessage = {
        id,
        author: by,
        text: trimmed,
        t: 'just now',
        ts: Date.now(),
        ...(mentions.length ? { mentions } : {}),
      }
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          chatReplies: { ...byParent, [parentId]: [...(byParent[parentId] ?? []), reply] },
          activityHead: [
            { t: 'just now', e: `${by} replied to a note` },
            ...(existing.activityHead ?? []),
          ],
        },
      }

      if (!req?.submissionUuid) return

      const parentMsg = (this.subjectMutations[key]?.chat ?? []).find((m) => m.id === parentId)
      const parentUuid = parentMsg?.uuid
      if (!parentUuid) {
        if (prior === undefined) {
          const { [key]: _removed, ...rest } = this.subjectMutations
          this.subjectMutations = rest
        } else {
          this.subjectMutations = { ...this.subjectMutations, [key]: prior }
        }
        toastWorkflowError('Parent note is not synced yet — try again')
        return
      }

      try {
        const snap = await postDdWorkflowChatReply({
          submissionUuid: req.submissionUuid,
          lineKey: sku,
          parentUuid,
          id,
          text: trimmed,
          mentions: mentions.length ? mentions : undefined,
        })
        const cur = this.subjectMutations[key] ?? {}
        const replies = cur.chatReplies ?? {}
        const serverReply = chatMessageFromSnapshot(snap)
        this.subjectMutations = {
          ...this.subjectMutations,
          [key]: {
            ...cur,
            chatReplies: {
              ...replies,
              [parentId]: (replies[parentId] ?? []).map((r) => (r.id === id ? serverReply : r)),
            },
          },
        }
      } catch (err) {
        if (prior === undefined) {
          const { [key]: _removed, ...rest } = this.subjectMutations
          this.subjectMutations = rest
        } else {
          this.subjectMutations = { ...this.subjectMutations, [key]: prior }
        }
        toastWorkflowError(err instanceof Error ? err.message : 'Chat reply failed')
      }
    },

    applyEstimationUpdate(update: EstimationUpdate): boolean {
      const req = this.findRequest(update.request_id)
      if (!req) return false

      const skus = req.isMaster ? req.children.map((c) => c.sku) : [(req as Single).sku]
      const piqPricing: Record<string, number> = { ...(req.piqPricing ?? {}) }
      const piqPricingCents: Record<string, number> = { ...(req.piqPricingCents ?? {}) }
      for (const line of update.lines ?? []) {
        const sku =
          line.sku && skus.includes(line.sku)
            ? line.sku
            : line.line_no != null
              ? skus[line.line_no - 1]
              : undefined
        if (sku) {
          piqPricing[sku] = line.unit_price
          piqPricingCents[sku] = Math.round(line.unit_price * 100)
        }
      }

      this.ingestedRequests = this.ingestedRequests.map((r) => {
        if (r.id !== update.request_id) return r
        return {
          ...r,
          quoteNumber: update.quote_number ?? r.quoteNumber,
          paymentTerms: update.payment_terms ?? r.paymentTerms,
          taxRate: update.tax_rate ?? r.taxRate,
          piqPricing,
          piqPricingCents,
        }
      })

      const mutations = { ...this.subjectMutations }
      for (const sku of skus) {
        const key = `${update.request_id}:${sku}`
        let mut = mutations[key] ?? {}
        const seed = req.isMaster ? req.children.find((c) => c.sku === sku) : (req as Single)
        const current =
          mut.deliverableUpdates?.estimation?.status ?? seed?.deliverables.estimation?.status
        if (current !== 'complete' && current !== 'not_required') {
          const seedDeliverables = seed?.deliverables ?? {}
          const estimationMode = mut.estimationMode ?? seed?.estimationMode ?? 'standard'
          const merged: Record<string, DeliverableEntry> = { ...seedDeliverables }
          for (const [k, partial] of Object.entries(mut.deliverableUpdates ?? {})) {
            merged[k] = {
              ...(merged[k] ?? { status: 'pending', assignee: null }),
              ...partial,
            }
          }
          merged.estimation = { status: 'complete', assignee: null }
          const promoted = resolveOpenDeliverables(merged, { estimationMode })
          const deliverableUpdates: Record<string, Partial<DeliverableEntry>> = {
            ...(mut.deliverableUpdates ?? {}),
            estimation: { status: 'complete', assignee: null },
          }
          for (const [k, entry] of Object.entries(promoted)) {
            const seedEntry = seedDeliverables[k]
            if (
              !seedEntry ||
              seedEntry.status !== entry.status ||
              seedEntry.assignee !== entry.assignee
            ) {
              deliverableUpdates[k] = entry
            }
          }
          mut = {
            ...mut,
            deliverableUpdates,
            deliverableCompletedAt: {
              ...(mut.deliverableCompletedAt ?? {}),
              estimation: Date.now(),
            },
            activityHead: [
              {
                t: 'just now',
                e: `PIQ quote ${update.quote_number ?? ''} received from HubSpot — Estimation complete`.replace(
                  '  ',
                  ' '
                ),
              },
              ...(mut.activityHead ?? []),
            ],
          }
          mutations[key] = mut
        }
      }
      this.subjectMutations = mutations
      return true
    },

    addProofVersion(
      requestId: string,
      sku: string,
      file: Omit<ProofVersion, 'id' | 'version' | 'uploadedBy' | 't' | 'deletedAt' | 'deletedBy'> &
        Partial<Pick<ProofVersion, 'id' | 'version' | 'uploadedBy' | 't'>>
    ) {
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      const prev = existing.proofVersions ?? []
      let lastActiveIdx = -1
      for (let i = prev.length - 1; i >= 0; i--) {
        if (!prev[i].deletedAt) {
          lastActiveIdx = i
          break
        }
      }
      const lastActive = lastActiveIdx >= 0 ? prev[lastActiveIdx] : null
      const uploader = file.uploadedBy ?? me()
      const stamp = file.t ?? 'just now'

      if (lastActive && lastActive.version === 0) {
        const updated = prev.map((p, i) =>
          i === lastActiveIdx
            ? {
                ...p,
                ...file,
                id: file.id ?? p.id,
                version: file.version ?? 0,
                t: stamp,
                uploadedBy: uploader,
                supportingFiles: file.supportingFiles ?? p.supportingFiles,
              }
            : p
        )
        this.subjectMutations = {
          ...this.subjectMutations,
          [key]: {
            ...existing,
            proofVersions: updated,
            activityHead: [
              { t: 'just now', e: `${me()} updated the working proof — ${file.name}` },
              ...(existing.activityHead ?? []),
            ],
          },
        }
        return
      }

      const preloadedSupporting = lastActive?.supportingFiles ?? []
      const pv: ProofVersion = {
        ...file,
        id: file.id ?? genProofId(),
        version: file.version ?? 0,
        uploadedBy: uploader,
        t: stamp,
        supportingFiles:
          preloadedSupporting.length > 0 ? [...preloadedSupporting] : undefined,
      }
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          proofVersions: [...prev, pv],
          activityHead: [
            { t: 'just now', e: `${me()} uploaded a proof — ${file.name}` },
            ...(existing.activityHead ?? []),
          ],
        },
      }
    },

    deleteProofVersion(requestId: string, sku: string, proofId: string) {
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      const prev = existing.proofVersions ?? []
      const target = prev.find((p) => p.id === proofId && !p.deletedAt)
      if (!target) return
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          proofVersions: prev.map((p) =>
            p.id === proofId
              ? { ...p, deletedAt: 'just now', deletedBy: me() }
              : p
          ),
          activityHead: [
            { t: 'just now', e: `${me()} deleted proof ${target.name}` },
            ...(existing.activityHead ?? []),
          ],
        },
      }
    },

    addSupportingFile(
      requestId: string,
      sku: string,
      file: { id: string; name: string; type: string; size: number; downloadUrl?: string; dataUrl?: string }
    ) {
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      const prev = existing.proofVersions ?? []
      let currentIdx = -1
      for (let i = prev.length - 1; i >= 0; i--) {
        if (!prev[i].deletedAt) {
          currentIdx = i
          break
        }
      }
      if (currentIdx < 0) return
      const updated = prev.map((p, i) =>
        i === currentIdx
          ? { ...p, supportingFiles: [...(p.supportingFiles ?? []), file] }
          : p
      )
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: { ...existing, proofVersions: updated },
      }
    },

    removeSupportingFile(requestId: string, sku: string, fileId: string) {
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      const prev = existing.proofVersions ?? []
      let currentIdx = -1
      for (let i = prev.length - 1; i >= 0; i--) {
        if (!prev[i].deletedAt) {
          currentIdx = i
          break
        }
      }
      if (currentIdx < 0) return
      const updated = prev.map((p, i) =>
        i === currentIdx
          ? {
              ...p,
              supportingFiles: (p.supportingFiles ?? []).filter((f) => f.id !== fileId),
            }
          : p
      )
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: { ...existing, proofVersions: updated },
      }
    },

    reorderSupportingFiles(requestId: string, sku: string, fromIdx: number, toIdx: number) {
      if (fromIdx === toIdx) return
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      const prev = existing.proofVersions ?? []
      let currentIdx = -1
      for (let i = prev.length - 1; i >= 0; i--) {
        if (!prev[i].deletedAt) {
          currentIdx = i
          break
        }
      }
      if (currentIdx < 0) return
      const updated = prev.map((p, i) => {
        if (i !== currentIdx) return p
        const files = [...(p.supportingFiles ?? [])]
        const [moved] = files.splice(fromIdx, 1)
        if (!moved) return p
        files.splice(toIdx, 0, moved)
        return { ...p, supportingFiles: files }
      })
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: { ...existing, proofVersions: updated },
      }
    },

    async setQuoteProof(requestId: string, sku: string, versionId: string) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const existing = this.subjectMutations[key] ?? {}
      const prior = this.subjectMutations[key]
      const target = (existing.proofVersions ?? []).find((p) => p.id === versionId)
      if (!target) return
      const expectedVersion = existing.workflowLineVersion ?? null
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          quotedProofId: versionId,
          activityHead: [
            {
              t: 'just now',
              e: `${me()} published Proof v${target.version} to the Proof Packet`,
            },
            ...(existing.activityHead ?? []),
          ],
        },
      }
      if (!req?.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        putDdWorkflowPacket({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          expectedVersion,
          quotedProofId: versionId,
        })
      )
    },

    async setPmsColors(requestId: string, sku: string, colors: string[]) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const existing = this.subjectMutations[key] ?? {}
      const prior = this.subjectMutations[key]
      const expectedVersion = existing.workflowLineVersion ?? null
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: { ...existing, pmsColors: colors },
      }
      if (!req?.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        putDdWorkflowPacket({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          expectedVersion,
          pmsColors: colors,
        })
      )
    },

    async setPacketNotes(requestId: string, sku: string, notes: string) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      const existing = this.subjectMutations[key] ?? {}
      const prior = this.subjectMutations[key]
      const expectedVersion = existing.workflowLineVersion ?? null
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: { ...existing, packetNotes: notes },
      }
      if (!req?.submissionUuid) return
      await this.persistWorkflowMutation(requestId, sku, prior, () =>
        putDdWorkflowPacket({
          submissionUuid: req.submissionUuid!,
          lineKey: sku,
          expectedVersion,
          packetNotes: notes,
        })
      )
    },

    addDesignFile(
      requestId: string,
      sku: string,
      category: string,
      file: Omit<DesignFile, 'id' | 'uploadedBy' | 't'> & Partial<Pick<DesignFile, 'id' | 'uploadedBy' | 't'>>
    ) {
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      const df: DesignFile = {
        ...file,
        id: file.id ?? genDesignFileId(),
        uploadedBy: file.uploadedBy ?? me(),
        t: file.t ?? 'just now',
      }
      const prev = existing.filesByCategory ?? {}
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          filesByCategory: {
            ...prev,
            [category]: [...(prev[category] ?? []), df],
          },
          activityHead: [
            { t: 'just now', e: `${me()} uploaded ${file.name}` },
            ...(existing.activityHead ?? []),
          ],
        },
      }
    },

    /**
     * DD-BE-PR2b-FILES: replace design-file / proof overlays for one subject from API list.
     * Does not clear assign/complete/chat/timers fields.
     */
    applyPersistedFiles(requestId: string, sku: string, files: DesignDevelopmentQueueFileApi[]) {
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      const filesByCategory: Record<string, DesignFile[]> = {}
      const mains: ProofVersion[] = []
      let quoteFile: DesignFile | null = null
      const supportingByParent = new Map<
        string,
        NonNullable<ProofVersion['supportingFiles']>
      >()

      for (const f of files) {
        if (f.kind === 'design_file' && f.category) {
          const row: DesignFile = {
            id: f.uuid,
            name: f.original_name,
            type: f.mime ?? 'application/octet-stream',
            size: f.size,
            downloadUrl: f.download_url,
            uploadedBy: f.uploaded_by_name ?? 'Unknown',
            t: f.uploaded_at ?? '',
          }
          filesByCategory[f.category] = [...(filesByCategory[f.category] ?? []), row]
        } else if (f.kind === 'quote') {
          quoteFile = {
            id: f.uuid,
            name: f.original_name,
            type: f.mime ?? 'application/octet-stream',
            size: f.size,
            downloadUrl: f.download_url,
            uploadedBy: f.uploaded_by_name ?? 'Unknown',
            t: f.uploaded_at ?? '',
          }
        } else if (f.kind === 'proof' && f.parent_uuid) {
          const list = supportingByParent.get(f.parent_uuid) ?? []
          list.push({
            id: f.uuid,
            name: f.original_name,
            type: f.mime ?? 'application/octet-stream',
            size: f.size,
            downloadUrl: f.download_url,
          })
          supportingByParent.set(f.parent_uuid, list)
        } else if (f.kind === 'proof') {
          mains.push({
            id: f.uuid,
            version: f.proof_version ?? 0,
            name: f.original_name,
            type: f.mime ?? 'application/octet-stream',
            size: f.size,
            downloadUrl: f.download_url,
            uploadedBy: f.uploaded_by_name ?? 'Unknown',
            t: f.uploaded_at ?? '',
            packetSnapshot: f.packet_snapshot ?? undefined,
            source:
              f.packet_snapshot && typeof f.packet_snapshot.source === 'string'
                ? (f.packet_snapshot.source as 'design' | 'estimate')
                : undefined,
          })
        }
      }

      for (const main of mains) {
        const sf = supportingByParent.get(main.id)
        if (sf?.length) {
          main.supportingFiles = sf
        }
      }

      mains.sort((a, b) => a.version - b.version)

      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          filesByCategory,
          proofVersions: mains,
          quoteFile: quoteFile,
        },
      }
    },

    async hydrateSubjectFiles(requestId: string, sku: string): Promise<void> {
      const req = this.ingestedRequests.find((r) => r.id === requestId)
      const submissionUuid = req?.submissionUuid
      if (!submissionUuid) return
      const files = await listDesignDevelopmentQueueFiles(submissionUuid, sku)
      this.applyPersistedFiles(requestId, sku, files)
    },

    removeDesignFile(requestId: string, sku: string, category: string, fileId: string) {
      const key = `${requestId}:${sku}`
      const existing = this.subjectMutations[key] ?? {}
      const prev = existing.filesByCategory ?? {}
      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          filesByCategory: {
            ...prev,
            [category]: (prev[category] ?? []).filter((f) => f.id !== fileId),
          },
        },
      }
    },

    applySalesApproval(requestId: string, sku: string, by?: string) {
      const key = `${requestId}:${sku}`
      const req = this.findRequest(requestId)
      if (!req) return

      const existing = this.subjectMutations[key] ?? {}
      const resolved = resolveSubject(req, sku, this.subjectMutations)
      if (!resolved) return
      if (resolved.deliverables.sales_approval?.status === 'complete') return

      const rep = by ?? 'Sales'
      let newDeliverables = advanceDeliverable(
        resolved.deliverables,
        'sales_approval',
        { estimationMode: resolved.estimationMode }
      )
      newDeliverables = autoCompleteOpen(newDeliverables, resolved.estimationMode)
      const gate = newDeliverables.sales_approval
      if (gate) {
        newDeliverables = { ...newDeliverables, sales_approval: { ...gate, assignee: rep } }
      }

      const seedSubject = req.isMaster
        ? req.children.find((c) => c.sku === sku)
        : req
      const deltaUpdates: Record<string, Partial<DeliverableEntry>> = {}
      if (seedSubject) {
        for (const [k, entry] of Object.entries(newDeliverables)) {
          const seed = seedSubject.deliverables[k]
          if (seed?.status !== entry.status || seed?.assignee !== entry.assignee) {
            deltaUpdates[k] = entry
          }
        }
      }

      this.subjectMutations = {
        ...this.subjectMutations,
        [key]: {
          ...existing,
          deliverableUpdates: { ...(existing.deliverableUpdates ?? {}), ...deltaUpdates },
          deliverableOpenedAt: stampOpenedAt(
            resolved.deliverables,
            newDeliverables,
            existing.deliverableOpenedAt
          ),
          activityHead: [
            { t: 'just now', e: `${rep} approved Sales Review` },
            ...(existing.activityHead ?? []),
          ],
        },
      }
    },
  },
})
